import React, { useState, useRef } from 'react';
import SearchBar from './SearchBar';
import ProductCard from './ProductCard';
import TrackerList from './TrackerList';
import { useProductSearch } from '../hooks/useProductSearch';
import { usePriceTracker } from '../hooks/usePriceTracker';

/** 메인 앱 컴포넌트 */
export default function App() {
  const { products, isLoading, error, message, suggestedQuery, search } = useProductSearch();
  const { tracked, addQuery, removeQuery, isQueryTracked, refreshPrices } = usePriceTracker();
  const [activeTab, setActiveTab] = useState<'search' | 'tracker'>('search');
  const lastQueryRef = useRef('');

  const validProducts = products.filter((p) => !p.isSuspicious);
  const suspiciousProducts = products.filter((p) => p.isSuspicious);

  /** 검색 실행 + 마지막 검색어 기억 */
  const handleSearch = (query: string, sort?: string) => {
    lastQueryRef.current = query.trim();
    search(query, sort);
  };

  /** 추적 목록에서 검색 탭으로 전환 */
  const handleSearchFromTracker = (query: string) => {
    setActiveTab('search');
    lastQueryRef.current = query;
    search(query);
  };

  const currentQuery = lastQueryRef.current;
  const isCurrentTracked = currentQuery ? isQueryTracked(currentQuery) : false;

  return (
    <div className="app">
      <header className="app-header">
        <h1>🔍 최저가 파인더</h1>
        <p>네이버 쇼핑에서 진짜 최저가를 찾아드립니다</p>
      </header>

      <div className="tab-bar">
        <button
          className={`tab-button ${activeTab === 'search' ? 'active' : ''}`}
          onClick={() => setActiveTab('search')}
        >
          🔍 검색
        </button>
        <button
          className={`tab-button ${activeTab === 'tracker' ? 'active' : ''}`}
          onClick={() => { setActiveTab('tracker'); refreshPrices(); }}
        >
          📌 추적 목록 ({tracked.length})
        </button>
      </div>

      <main>
        {activeTab === 'search' && (
          <>
            <SearchBar onSearch={handleSearch} isLoading={isLoading} />

            {/* 검색어 추적 버튼: 검색 결과가 있을 때만 표시 */}
            {currentQuery && validProducts.length > 0 && (
              <div className="track-query-bar">
                {isCurrentTracked ? (
                  <span className="track-query-status">📌 "{currentQuery}" 추적 중</span>
                ) : (
                  <button
                    className="track-query-button"
                    onClick={() => addQuery(currentQuery)}
                  >
                    📌 "{currentQuery}" 검색어 추적하기
                  </button>
                )}
              </div>
            )}

            {error && (
              <div className="error-message" role="alert">{error}</div>
            )}

            {message && !error && (
              <p className="result-message">{message}</p>
            )}

            {suggestedQuery && (
              <div className="suggestion-box">
                <p>💡 더 정확한 결과를 원하시면:</p>
                <button
                  className="suggestion-button"
                  onClick={() => handleSearch(suggestedQuery)}
                >
                  "{suggestedQuery}" 로 검색하기
                </button>
              </div>
            )}

            {validProducts.length > 0 && (
              <section aria-label="검색 결과">
                <h2>추천 최저가 상품</h2>
                <div className="product-list">
                  {validProducts.map((product, index) => (
                    <ProductCard
                      key={product.productId}
                      product={product}
                      rank={index + 1}
                    />
                  ))}
                </div>
              </section>
            )}

            {suspiciousProducts.length > 0 && (
              <section aria-label="주의 상품" className="suspicious-section">
                <h2>⚠️ 주의가 필요한 상품 ({suspiciousProducts.length}개)</h2>
                <p className="suspicious-desc">
                  악세사리, 옵션 가격 낚시 등으로 판단된 상품입니다.
                </p>
                <div className="product-list">
                  {suspiciousProducts.map((product, index) => (
                    <ProductCard
                      key={product.productId}
                      product={product}
                      rank={index + 1}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {activeTab === 'tracker' && (
          <TrackerList
            tracked={tracked}
            onRemove={removeQuery}
            onSearch={handleSearchFromTracker}
          />
        )}
      </main>

      <footer className="app-footer">
        <details>
          <summary>📋 업데이트 이력</summary>
          <ul className="changelog">
            <li><code>NEW</code> 가격 추적 이력 그래프 표시 (30일, Y축 오토스케일)</li>
            <li><code>NEW</code> 검색어 기반 최저가 추적으로 전면 변경</li>
            <li><code>NEW</code> Cron Worker 기반 자동 가격 추적 (매일 오전 9시)</li>
            <li><code>NEW</code> 판매중지/재고없음/품절 상품 필터링 추가</li>
            <li><code>85e66bf</code> 통신사 약정 확장 + 악세사리 단어 감지 필터링 강화</li>
            <li><code>0050fad</code> 검색 결과 부정확 시 추천 검색어 표시</li>
            <li><code>5e5918a</code> 추정 할인 제거, 상품가만 표시</li>
            <li><code>669513a</code> 카테고리 그룹핑 + 상위30% 가격 기준 전면 재설계</li>
            <li><code>7339f44</code> T2/T3 직접구매 우선 정렬</li>
            <li><code>9bb193c</code> T1 가격비교 탭 링크로 변경</li>
            <li><code>0173691</code> 최저가 파인더 프로젝트 초기 생성</li>
          </ul>
        </details>
      </footer>
    </div>
  );
}
